import { useEffect, useState } from 'react';
import { Briefcase, TrendingUp, TrendingDown, Trash2, PieChart, DollarSign, Percent } from 'lucide-react';
import { Stock, Portfolio } from '../lib/supabase';
import { marketService } from '../lib/marketService';
import { useAuth } from '../contexts/AuthContext';

interface PortfolioViewProps {
  refresh?: number;
}

export const PortfolioView = ({ refresh }: PortfolioViewProps) => {
  const [portfolio, setPortfolio] = useState<Portfolio[]>([]);
  const [stocks, setStocks] = useState<Record<string, Stock>>({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) loadPortfolio();
    else setLoading(false);
  }, [user, refresh]);

  const loadPortfolio = async () => {
    if (!user) return;
    try {
      const portfolioData = await marketService.getPortfolio(user.id);
      setPortfolio(portfolioData);
      const allStocks = await marketService.getStocks();
      const stocksMap: Record<string, Stock> = {};
      allStocks.forEach(stock => { stocksMap[stock.code] = stock; });
      setStocks(stocksMap);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromPortfolio = async (portfolioId: string) => {
    if (!confirm('Hapus posisi ini dari portfolio?')) return;
    try {
      await marketService.removeFromPortfolio(portfolioId);
      setPortfolio(portfolio.filter(p => p.id !== portfolioId));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const stats = (() => {
    let totalValue = 0, totalCost = 0;
    portfolio.forEach(item => {
      const stock = stocks[item.stock_code];
      if (stock) {
        totalValue += stock.price * item.quantity;
        totalCost += item.avg_price * item.quantity;
      }
    });
    const gainLoss = totalValue - totalCost;
    const gainLossPercent = totalCost > 0 ? (gainLoss / totalCost) * 100 : 0;
    return { totalValue, totalCost, gainLoss, gainLossPercent };
  })();

  if (!user) {
    return (
      <div className="card p-12 text-center">
        <Briefcase className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Portfolio</h3>
        <p className="text-slate-400">Silakan login untuk menggunakan fitur portfolio</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (portfolio.length === 0) {
    return (
      <div className="card p-12 text-center">
        <Briefcase className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Portfolio Kosong</h3>
        <p className="text-slate-400">Tambahkan saham ke portfolio dari detail saham</p>
      </div>
    );
  }

  const isProfit = stats.gainLoss >= 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Nilai', value: `Rp ${marketService.formatPrice(stats.totalValue)}`, icon: PieChart, color: 'text-white' },
          { label: 'Modal', value: `Rp ${marketService.formatPrice(stats.totalCost)}`, icon: DollarSign, color: 'text-slate-400' },
          { label: 'Gain/Loss', value: `${isProfit ? '+' : ''}Rp ${marketService.formatPrice(Math.abs(stats.gainLoss))}`, icon: TrendingUp, color: isProfit ? 'text-emerald-400' : 'text-red-400' },
          { label: 'Return', value: `${isProfit ? '+' : ''}${stats.gainLossPercent.toFixed(2)}%`, icon: Percent, color: isProfit ? 'text-emerald-400' : 'text-red-400' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="card p-4">
              <div className="flex items-center space-x-2 text-slate-500 mb-2">
                <Icon className="w-4 h-4" />
                <span className="text-xs font-medium">{stat.label}</span>
              </div>
              <div className={`text-xl font-bold font-mono ${stat.color}`}>{stat.value}</div>
            </div>
          );
        })}
      </div>

      <div className="space-y-3">
        {portfolio.map((item) => {
          const stock = stocks[item.stock_code];
          if (!stock) return null;

          const currentValue = stock.price * item.quantity;
          const cost = item.avg_price * item.quantity;
          const gainLoss = currentValue - cost;
          const gainLossPercent = (gainLoss / cost) * 100;
          const isItemProfit = gainLoss >= 0;

          return (
            <div key={item.id} className="card p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                    <span className="font-bold text-sm text-emerald-400">{stock.code.slice(0, 2)}</span>
                  </div>
                  <div>
                    <div className="font-bold text-white">{stock.code}</div>
                    <div className="text-xs text-slate-500">{stock.name}</div>
                  </div>
                </div>
                <button onClick={() => removeFromPortfolio(item.id)} className="p-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                {[
                  { label: 'Qty', value: `${item.quantity} lot` },
                  { label: 'Avg Price', value: marketService.formatPrice(item.avg_price) },
                  { label: 'Current', value: marketService.formatPrice(stock.price) },
                  { label: 'Value', value: `Rp ${marketService.formatPrice(currentValue)}` },
                  { label: 'P/L', value: `${isItemProfit ? '+' : ''}${gainLossPercent.toFixed(2)}%`, color: isItemProfit ? 'text-emerald-400' : 'text-red-400' },
                ].map((col) => (
                  <div key={col.label}>
                    <div className="text-xs text-slate-500">{col.label}</div>
                    <div className={`font-mono font-semibold ${(col as any).color || 'text-white'}`}>{col.value}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
